import React from 'react';
import {
  X,
  Check,
  Shield,
  Zap,
  Unlock,
  Eye,
  Code,
  DollarSign,
  Sparkles,
  Clock,
  HelpCircle,
} from 'lucide-react';

interface PricingPageProps {
  isOpen: boolean;
  onClose: () => void;
  onGetLicense: () => void;
}

export const PricingPage: React.FC<PricingPageProps> = ({
  isOpen,
  onClose,
  onGetLicense,
}) => {
  if (!isOpen) return null;

  const freeFeatures = [
    'Upload and visualize unlimited Zapier workflows',
    'Interactive workflow graph with zoom and pan',
    'View workflow structure and step sequence',
    'See app names and action types',
    'Dark mode support',
    '100% client-side processing - your data never leaves your browser',
  ];

  const proFeatures = [
    'View complete step configurations',
    'Access all mapped variables and field references',
    'See code snippets and filter logic',
    'Export and copy raw node data',
    'Inspect detailed input/output mappings',
    'Lifetime access - pay once, use forever',
  ];

  const faqs = [
    {
      question: 'Is my data really private?',
      answer:
        'Yes! All JSON parsing happens 100% in your browser using the FileReader API. Your Zapier workflow files never leave your device. We never upload, store, or transmit your data to any server.',
    },
    {
      question: 'What is a lifetime license?',
      answer:
        'Pay $29 once and get unlimited access forever. No subscriptions, no recurring fees, no hidden costs. Use ZapLens Pro as long as you need it.',
    },
    {
      question: 'How does this compare to Zapier Team?',
      answer:
        "Zapier Team costs $240/year just to import workflows. ZapLens costs $29 once and lets you view all your workflow details. That's 89% cheaper with lifetime access.",
    },
    {
      question: 'What if I only need to view one workflow?',
      answer:
        'The free tier is perfect for basic visualization! You can see your workflow structure, app connections, and step sequence without paying anything. Upgrade to Pro when you need detailed configuration access.',
    },
  ];

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 overflow-y-auto animate-fade-in">
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-card/50 backdrop-blur-sm border-b border-border z-10">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-6 h-6 text-primary" />
              <h1 className="text-xl font-bold">ZapLens</h1>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-accent rounded-lg transition-colors"
              aria-label="Close pricing page"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 max-w-5xl mx-auto px-6 py-12 w-full">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Simple, Transparent Pricing
            </div>
            <h2 className="text-5xl font-bold mb-4">
              View Zapier Workflows
              <br />
              Without Breaking the Bank
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              89% cheaper than Zapier Team ($240/year). Pay once, use forever.
            </p>
          </div>

          {/* Privacy Banner */}
          <div className="mb-12 p-6 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <h3 className="font-semibold text-emerald-900 dark:text-emerald-100">
                Privacy-First Architecture
              </h3>
            </div>
            <p className="text-sm text-emerald-800 dark:text-emerald-200">
              All workflow parsing happens in your browser. Your data never leaves your device.
              No uploads, no servers, no tracking.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {/* Free Tier */}
            <div className="bg-card border border-border rounded-xl p-8 relative">
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2">Free</h3>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-5xl font-bold">$0</span>
                  <span className="text-muted-foreground">forever</span>
                </div>
                <p className="text-muted-foreground">
                  Perfect for visualizing workflow structure and connections
                </p>
              </div>

              <div className="space-y-3 mb-8">
                {freeFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="pt-6 border-t border-border">
                <button
                  onClick={onClose}
                  className="w-full px-6 py-3 bg-muted hover:bg-accent rounded-lg transition-colors font-medium"
                >
                  Start Using Free
                </button>
              </div>
            </div>

            {/* Pro Tier - Featured */}
            <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-2 border-primary rounded-xl p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground rounded-full text-xs font-bold uppercase tracking-wide">
                Best Value
              </div>

              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2">Pro</h3>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-5xl font-bold">$29</span>
                  <span className="text-muted-foreground">one-time</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                  <Clock className="w-4 h-4" />
                  <span>Lifetime access - no recurring fees</span>
                </div>
                <p className="text-muted-foreground">
                  Unlock complete workflow details and configurations
                </p>
              </div>

              <div className="bg-card/50 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium mb-2">Everything in Free, plus:</p>
                <div className="space-y-3">
                  {proFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={onGetLicense}
                  className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-bold text-lg shadow-lg shadow-primary/20"
                >
                  Get Pro License - $29
                </button>
                <p className="text-xs text-center text-muted-foreground">
                  Instant delivery • Secure checkout by Lemon Squeezy
                </p>
              </div>
            </div>
          </div>

          {/* Feature Comparison */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-center mb-8">What You Get</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-card border border-border rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Eye className="w-6 h-6 text-blue-500" />
                </div>
                <h4 className="font-semibold mb-2">Free: Visual Overview</h4>
                <p className="text-sm text-muted-foreground">
                  See the big picture - workflow structure, app connections, and step sequence
                </p>
              </div>

              <div className="bg-card border border-border rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Unlock className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Pro: Full Details</h4>
                <p className="text-sm text-muted-foreground">
                  Access complete configurations, mapped variables, and all step details
                </p>
              </div>

              <div className="bg-card border border-border rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Code className="w-6 h-6 text-purple-500" />
                </div>
                <h4 className="font-semibold mb-2">Pro: Code & Exports</h4>
                <p className="text-sm text-muted-foreground">
                  View code snippets, filter logic, and export raw JSON data
                </p>
              </div>
            </div>
          </div>

          {/* Value Proposition */}
          <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-8 mb-16 text-center">
            <DollarSign className="w-12 h-12 text-amber-600 dark:text-amber-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-3">Save 89% vs Zapier Team</h3>
            <div className="max-w-2xl mx-auto">
              <p className="text-muted-foreground mb-6">
                Zapier Team costs <strong className="text-foreground">$240/year</strong> just to
                import workflows. ZapLens gives you lifetime access to view all your workflow
                details for a <strong className="text-foreground">one-time $29 payment</strong>.
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-card/50 rounded-lg p-4">
                  <p className="text-muted-foreground mb-1">Zapier Team (1 year)</p>
                  <p className="text-2xl font-bold">$240</p>
                </div>
                <div className="bg-card/50 rounded-lg p-4 border-2 border-primary">
                  <p className="text-muted-foreground mb-1">ZapLens Pro (lifetime)</p>
                  <p className="text-2xl font-bold text-primary">$29</p>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mb-12">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-2">Frequently Asked Questions</h3>
              <p className="text-muted-foreground">
                Everything you need to know about ZapLens
              </p>
            </div>

            <div className="space-y-4 max-w-3xl mx-auto">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="bg-card border border-border rounded-lg p-6 group"
                >
                  <summary className="font-semibold cursor-pointer flex items-center justify-between">
                    <span className="flex items-center gap-3">
                      <HelpCircle className="w-5 h-5 text-primary flex-shrink-0" />
                      {faq.question}
                    </span>
                    <svg
                      className="w-5 h-5 transition-transform group-open:rotate-180"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </summary>
                  <p className="mt-4 text-muted-foreground pl-8">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center bg-card border border-border rounded-xl p-12">
            <h3 className="text-3xl font-bold mb-4">Ready to unlock Pro features?</h3>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Get lifetime access to view complete workflow configurations, mapped variables,
              code snippets, and more.
            </p>
            <button
              onClick={onGetLicense}
              className="px-8 py-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-bold text-lg shadow-lg shadow-primary/20 inline-flex items-center gap-2"
            >
              <Zap className="w-5 h-5" />
              Get Pro License - $29
            </button>
            <p className="text-sm text-muted-foreground mt-4">
              Instant delivery • Lifetime access • No subscription
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
